import React from 'react';
import { format } from 'date-fns';
import { Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Badge from '../ui/Badge';
import { PaymentHistory as PaymentHistoryType } from '../../types';

interface PaymentHistoryProps {
  payments: PaymentHistoryType[];
  showHeader?: boolean;
  compact?: boolean;
  className?: string;
}

const PaymentHistory: React.FC<PaymentHistoryProps> = ({
  payments,
  showHeader = true,
  compact = false,
  className = ''
}) => {
  // Calculate payment statistics
  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const averagePayment = totalPaid / (payments.length || 1);
  const onTimePayments = payments.filter(p => p.status === 'completed').length;
  const paymentReliability = (onTimePayments / (payments.length || 1)) * 100;

  return (
    <Card className={className}>
      {showHeader && (
        <CardHeader>
          <CardTitle className="text-base">Payment History</CardTitle>
        </CardHeader>
      )}
      <CardContent className={compact ? 'p-3' : 'p-4'}>
        {!compact && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <span className="text-sm text-gray-500 dark:text-gray-400">Total Paid</span>
              <p className="text-lg font-semibold mt-1">${totalPaid.toFixed(2)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <span className="text-sm text-gray-500 dark:text-gray-400">Average Payment</span>
              <p className="text-lg font-semibold mt-1">${averagePayment.toFixed(2)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <span className="text-sm text-gray-500 dark:text-gray-400">On-time Payments</span>
              <p className="text-lg font-semibold mt-1">{onTimePayments} of {payments.length}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <span className="text-sm text-gray-500 dark:text-gray-400">Payment Reliability</span>
              <p className="text-lg font-semibold mt-1">{paymentReliability.toFixed(1)}%</p>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Status</th>
                {!compact && <th className="px-3 py-2 text-left">Method</th>}
                <th className="px-3 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {payments.map((payment) => (
                <tr key={payment.id} className="bg-white dark:bg-gray-800">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 text-gray-400 mr-1" />
                      {format(new Date(payment.date), 'MMM d, yyyy')}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Badge 
                      variant={
                        payment.status === 'completed' ? 'success' :
                        payment.status === 'pending' ? 'warning' : 'danger'
                      }
                      size="sm"
                    >
                      {payment.status}
                    </Badge>
                  </td>
                  {!compact && <td className="px-3 py-2">{payment.paymentMethod}</td>}
                  <td className="px-3 py-2 text-right font-medium">
                    ${payment.amount.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            {!compact && (
              <tfoot className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-right font-medium">Total</td>
                  <td className="px-3 py-2 text-right font-medium">${totalPaid.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentHistory;